/**
 * One-time passkey registration. The provisioning token arrives in the URL
 * fragment (#token=…) so it never hits server logs; this page exchanges it
 * for WebAuthn creation options and registers the admin's first passkey.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { startRegistration } from "@simplewebauthn/browser";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button, Input } from "../components/ui";

export default function Register() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [who, setWho] = useState<{ email: string; name: string } | null>(null);
  const [deviceName, setDeviceName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const match = /[#&]token=([^&]+)/.exec(window.location.hash);
    if (match) {
      setToken(match[1]);
      // Drop the token from the address bar once captured.
      history.replaceState(null, "", window.location.pathname);
    } else {
      setError("Missing registration token. Ask a super admin for a fresh link.");
    }
  }, []);

  async function handleRegister() {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const { challengeId, options, admin } = await api.post<{
        challengeId: string;
        options: Parameters<typeof startRegistration>[0]["optionsJSON"];
        admin: { email: string; name: string };
      }>("/auth/register/options", { token });
      setWho(admin);
      const response = await startRegistration({ optionsJSON: options });
      await api.post("/auth/register/verify", {
        token,
        challengeId,
        response,
        deviceName: deviceName.trim() || null,
      });
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-rail-950 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center text-white">
          <h1 className="text-xl font-bold">RailAssurance Admin</h1>
          <p className="mt-1 text-sm text-rail-300">Register your passkey</p>
        </div>
        <div className="space-y-4 rounded-lg bg-white p-6 shadow-xl">
          {who && (
            <p className="text-sm text-slate-600">
              Registering a passkey for <span className="font-medium">{who.email}</span>
            </p>
          )}
          <div>
            <label htmlFor="device" className="mb-1 block text-xs font-medium text-slate-600">
              Device name (optional)
            </label>
            <Input
              id="device"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="MacBook Pro, YubiKey 5…"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <Button onClick={handleRegister} disabled={busy || !token} className="w-full">
            {busy ? "Waiting for your device…" : "Create passkey"}
          </Button>
          <p className="text-xs text-slate-500">
            This link is single-use. Once your passkey is created you'll be signed in and the link
            stops working.
          </p>
        </div>
      </div>
    </div>
  );
}
